/**
 * Per-post editorial overrides — data lives in `postOverrides.json`.
 *
 * This file is now a thin validated shim:
 *   - The types (`PostOverride`, `PostOverridePhoto`, `PhotoCredit`,
 *     `PhotoCaption`) stay here so every downstream import keeps working
 *     unchanged.
 *   - The data is read from `postOverrides.json` and validated against
 *     the Zod schema at module-load time. Any shape drift throws a
 *     descriptive Zod error naming the failing slug + field path, so a
 *     bad hand-edit or a malformed admin write can't reach runtime.
 *
 * Edit data in `postOverrides.json`, not here. Add rationale for each
 * edit to `postOverrides.notes.md` so future maintainers know the "why".
 *
 * Render-side consumers (`Posts.tsx`, `PostDetail.tsx` via
 * `lib/postOverrides.ts`) use `POST_OVERRIDES` exactly as before —
 * `mergePost` + `isOverrideExcluded` are unchanged.
 */

import rawData from "./postOverrides.json";
import { parsePostOverrides } from "./postOverrides.schema";

/**
 * Per-photo caption — paired by INDEX with the post's photos array.
 * Each entry lights up a "photo essay" gallery layout on PostDetail.
 */
export interface PhotoCaption {
  ar?: string;
  en?: string;
}

/**
 * Licensing credit shown beneath an attached stock image. For CC-licensed
 * Wikimedia Commons reuse we must (a) name the author, (b) name the
 * licence with a link to its deed, and (c) link back to the source file.
 */
export interface PhotoCredit {
  author: string;
  license: string;
  licenseUrl?: string;
  sourceUrl?: string;
  /** Nice display name for the origin — defaults to "Wikimedia Commons". */
  source?: string;
}

/**
 * A stock image attached to a post via override. `src` is the basename
 * under public/stock/ — the full path is built as stock/<src>-full.jpg
 * and stock/<src>-thumb.jpg. Both variants must exist (produced by
 * scripts/process-stock-images.js).
 */
export interface PostOverridePhoto {
  src: string;
  alt: string;
  credit: PhotoCredit;
}

export interface PostOverride {
  titleAr?: string;
  titleEn?: string;
  textAr?: string;
  textEn?: string;
  /**
   * Stock / Wikimedia photographs attached to a post that the original
   * Facebook entry did not include. mergePost promotes these into the
   * post's photos array when the base post has none.
   */
  photos?: PostOverridePhoto[];
  /** When set, switches PostDetail to the photo-essay layout. */
  photoCaptions?: PhotoCaption[];
  /**
   * Indices into the BASE post's photos array (unmodified, as imported
   * from Facebook) to exclude from the public view. Filtering happens in
   * `mergePost` before the stock-photo overlay logic runs, so the
   * resulting `merged.photos` array is what the renderer sees and what
   * `photoCaptions` is paired against by index.
   *
   * Non-destructive: the underlying JPEGs in `public/facebook-media/`
   * stay on disk; only the rendered list is filtered.
   */
  removedPhotos?: number[];
  exclude?: boolean;
  classification?: string;
}

export const POST_OVERRIDES: Record<string, PostOverride> =
  parsePostOverrides(rawData);
